import { css } from '@emotion/react';
import { MouseEventHandler } from 'react';

const CONSTANTS = {
  BUTTON_SIZE: {
    LARGE: 'large',
    SMALL: 'small'
  },
  BUTTON_TYPE: {
    PRIMARY: 'primary',
    SECONDARY: 'secondary'
  },
  BUTTON_COLORS: {
    DARK_BLUE: 'darkblue',
    YELLOW: 'yellow',
    GREEN: 'green',
    LIGHT_BLUE: 'lightblue',
    GRAY: 'gray',
    WHITE: 'white',
    TRANSPARENT: 'transparent'
  },
  DISABLED: 'disabled'
};

interface ButtonStyle {
  textColor: string;
  buttonBackgroundColor: string;
  buttonHoverColor: string;
  buttonFocusColor: string;
  border: string;
}

interface ButtonStyles {
  primary: {
    darkblue: ButtonStyle;
    yellow: ButtonStyle;
    green: ButtonStyle;
    lightblue: ButtonStyle;
    gray: ButtonStyle;
    disabled: ButtonStyle;
  };
  secondary: {
    darkblue: ButtonStyle;
    yellow: ButtonStyle;
    green: ButtonStyle;
    white: ButtonStyle;
    disabled: ButtonStyle;
  };
}

const buttonStyles: ButtonStyles = {
  primary: {
    // default styles
    darkblue: {
      textColor: '#FFF',
      buttonBackgroundColor: '#0052CC',
      buttonHoverColor: '#0047B3',
      buttonFocusColor: '#004099',
      border: 'none'
    },
    yellow: {
      textColor: '#172B4D',
      buttonBackgroundColor: '#FFAB00',
      buttonHoverColor: '#FF991F',
      buttonFocusColor: '#FF8B00',
      border: 'none'
    },
    green: {
      textColor: '#172B4D',
      buttonBackgroundColor: '#79F2C0',
      buttonHoverColor: '#57D9A3',
      buttonFocusColor: '#36B37E',
      border: 'none'
    },
    lightblue: {
      textColor: '#0052CC',
      buttonBackgroundColor: '#DEEBFF',
      buttonHoverColor: '#B3D4FF',
      buttonFocusColor: '#4C9AFF',
      border: 'none'
    },
    gray: {
      textColor: '#172B4D',
      buttonBackgroundColor: '#EBECF0',
      buttonHoverColor: '#DFE1E6',
      buttonFocusColor: '#C1C7D0',
      border: 'none'
    },
    disabled: {
      textColor: '#97A0AF',
      buttonBackgroundColor: '#EBECF0',
      buttonHoverColor: '',
      buttonFocusColor: '',
      border: 'none'
    }
  },
  secondary: {
    darkblue: {
      textColor: '#0052CC',
      buttonBackgroundColor: 'transparent',
      buttonHoverColor: '#EBECF0',
      buttonFocusColor: '#DEEBFF',
      border: '0.063rem solid #0052CC'
    },
    yellow: {
      textColor: '#172B4D',
      buttonBackgroundColor: 'transparent',
      buttonHoverColor: '#F4F5F7',
      buttonFocusColor: '#FFF0B3',
      border: '0.063rem solid #FFAB00'
    },
    green: {
      textColor: '#172B4D',
      buttonBackgroundColor: 'transparent',
      buttonHoverColor: '#F4F5F7',
      buttonFocusColor: '#E3FCEF',
      border: '0.063rem solid #79F2C0'
    },
    white: {
      textColor: '#FFFFFF',
      buttonBackgroundColor: 'transparent',
      buttonHoverColor: 'rgba(255, 255, 255, 0.10)',
      buttonFocusColor: 'rgba(255, 255, 255, 0.20)',
      border: '0.063rem solid #FFFFFF'
    },
    disabled: {
      textColor: '#97A0AF',
      buttonBackgroundColor: '#EBECF0',
      buttonHoverColor: '',
      buttonFocusColor: '',
      border: 'none'
    }
  }
};

const buttonTypes = [
  CONSTANTS.BUTTON_TYPE.PRIMARY,
  CONSTANTS.BUTTON_TYPE.SECONDARY
];
const buttonSizes = [CONSTANTS.BUTTON_SIZE.LARGE, CONSTANTS.BUTTON_SIZE.SMALL];
const buttonColors = [
  CONSTANTS.BUTTON_COLORS.DARK_BLUE,
  CONSTANTS.BUTTON_COLORS.YELLOW,
  CONSTANTS.BUTTON_COLORS.GREEN,
  CONSTANTS.BUTTON_COLORS.LIGHT_BLUE,
  CONSTANTS.BUTTON_COLORS.GRAY,
  CONSTANTS.BUTTON_COLORS.WHITE,
  CONSTANTS.BUTTON_COLORS.TRANSPARENT
];

const getButtonSize = (size: string | undefined): string => {
  const {
    BUTTON_SIZE: { LARGE }
  } = CONSTANTS;
  if (size) {
    const buttonSize = size.toLowerCase();
    return buttonSizes.includes(buttonSize) ? buttonSize : LARGE;
  }

  return LARGE;
};

const getButtonType = (type: string | undefined): string => {
  const buttonType = type?.toLowerCase();
  if (buttonType && buttonTypes.includes(buttonType)) {
    return buttonType;
  }

  return CONSTANTS.BUTTON_TYPE.PRIMARY;
};

const getButtonColor = (
  color: string | undefined,
  disabled: boolean | undefined
): string => {
  const {
    DISABLED,
    BUTTON_COLORS: { DARK_BLUE }
  } = CONSTANTS;

  if (disabled) {
    return DISABLED;
  }

  const buttonColor = color?.toLowerCase();

  if (buttonColor && buttonColors.includes(buttonColor)) {
    return buttonColor;
  }

  return DARK_BLUE;
};

const getButtonStyles = (color: string, type: string): ButtonStyle => {
  const buttonTypeStyles = buttonStyles[type as keyof ButtonStyles];
  const styles: ButtonStyle =
    buttonTypeStyles[color as keyof typeof buttonTypeStyles];

  if (styles) {
    return styles;
  }

  // return blue primary styles if style was not found
  return buttonStyles.primary.darkblue;
};

export interface ButtonProps {
  text: string;
  type?: 'primary' | 'secondary';
  color?: string;
  size?: 'large' | 'small';
  onClick?: MouseEventHandler;
  url?: string;
  disabled?: boolean;
  className?: string;
}

export const Button = ({
  text,
  type,
  color,
  size,
  onClick,
  url,
  disabled,
  className
}: ButtonProps) => {
  const buttonType: string = getButtonType(type);
  const buttonColor: string = getButtonColor(color, disabled);
  const {
    textColor,
    buttonBackgroundColor,
    buttonHoverColor,
    buttonFocusColor,
    border
  } = getButtonStyles(buttonColor, buttonType);
  const buttonSize: string = getButtonSize(size);
  const buttonHeight = buttonSize === CONSTANTS.BUTTON_SIZE.LARGE ? 2.5 : 2;
  const fontSize = buttonSize === CONSTANTS.BUTTON_SIZE.LARGE ? 1 : 0.875;
  const cursor = disabled ? 'cursor' : 'pointer';

  const serializedStyles = css`
    align-items: center;
    background: ${buttonBackgroundColor};
    border-radius: 0.25rem;
    box-sizing: border-box;
    border: ${border};
    cursor: ${cursor};
    color: ${textColor};
    display: inline-flex;
    font-weight: 600;
    font-size: ${fontSize}rem;
    font-family: var(--bxp-charlie-text-font-family);
    height: ${buttonHeight}rem;
    line-height: 1.25rem;
    padding: 0 1rem;
    text-align: center;
    text-decoration: none;
    :hover,
    :focus {
      background: ${buttonHoverColor};
    }
    :active {
      background: ${buttonFocusColor};
    }
  `;

  if (onClick && !url) {
    return (
      <button
        className={className}
        css={serializedStyles}
        onClick={onClick}
        disabled={disabled}
      >
        {text}
      </button>
    );
  }

  return (
    <a
      className={className}
      css={serializedStyles}
      onClick={onClick}
      href={url}
    >
      {text}
    </a>
  );
};
